type QueryPrimitive = string | number | boolean

export type QueryParamValue =
  | QueryPrimitive
  | unknown[]
  | Record<string, unknown>
  | null
  | undefined

type BuildQueryParamsOptions = {
  bracketArrayKeys?: string[]
  nestedBracketArrayKeys?: string[]
}

const isEmptyValue = (value: unknown) => {
  return value === undefined || value === null || value === ''
}

export const buildQueryParams = (
  params: Record<string, QueryParamValue>,
  options?: BuildQueryParamsOptions
) => {
  const searchParams = new URLSearchParams()
  const bracketArrayKeys = new Set(options?.bracketArrayKeys || [])
  const nestedBracketArrayKeys = new Set(options?.nestedBracketArrayKeys || [])

  Object.entries(params).forEach(([key, value]) => {
    if (isEmptyValue(value)) return

    if (Array.isArray(value)) {
      if (value.length === 0) return

      // Handle nested arrays like bounding_box[0][0]=val&bounding_box[0][1]=val
      if (nestedBracketArrayKeys.has(key)) {
        value.forEach((innerArray, outerIndex) => {
          if (Array.isArray(innerArray)) {
            innerArray.forEach((item, innerIndex) => {
              if (isEmptyValue(item)) return
              searchParams.append(`${key}[${outerIndex}][${innerIndex}]`, String(item))
            })
          } else if (!isEmptyValue(innerArray)) {
            searchParams.append(`${key}[${outerIndex}]`, String(innerArray))
          }
        })
        return
      }

      const queryKey = bracketArrayKeys.has(key) ? `${key}[]` : key

      value.forEach((item) => {
        if (isEmptyValue(item)) return
        searchParams.append(queryKey, String(item))
      })

      return
    }

    if (typeof value === 'object') {
      searchParams.append(key, JSON.stringify(value))
      return
    }

    searchParams.append(key, String(value))
  })

  return searchParams
}

export const withQueryParams = (
  url: string,
  params: Record<string, QueryParamValue>,
  options?: BuildQueryParamsOptions
) => {
  const queryString = buildQueryParams(params, options).toString()
  return `${url}${queryString ? `?${queryString}` : ''}`
}
